import React from 'react';
import { useTranslation } from 'react-i18next';
import MiniFAQ from '../MiniPDP/MiniFAQ/MiniFAQ';
import FAQ from '../../views/ProductDetails/Series3CollarDetails/components/FAQ';
import HardwareFirstAppCarousel from './HardwareFirstAppCarousel';
import { Species } from '../../types/gql-op-types';
import * as types from '../../types';
import HardwareFirstBehaviors from './HardwareFirstBehaviors';
import {
  HardwareFirstAllFeatures,
  HardwareFirstOwnersSay,
  HardwareFirstQuizBand,
  HardwareFirstSpecs,
} from './HardwareFirstSections';
import styles from './HardwareFirstPickYourFi.module.scss';

export type PickYourFiDeviceKey = 'mini' | 's3' | 'ultra';

/** Each device's kit, one white card per item, matching the figma's single-row box layout. */
export const BOX_CONTENTS: Record<PickYourFiDeviceKey, { url: string; labelKey: string }[]> = {
  mini: [
    { url: '/product_images/mini/box/device.png', labelKey: 'whats_in_the_box_mini_device' },
    { url: '/product_images/mini/box/cases.png', labelKey: 'whats_in_the_box_silicone_cases' },
    { url: '/product_images/mini/box/cable.png', labelKey: 'whats_in_the_box_charging_cord' },
  ],
  s3: [
    { url: '/product_images/smart-collar-v3b/gray.png', labelKey: 'cart_item_v2.series_3_device' },
    { url: '/product_images/additional-band-s3/gray.png', labelKey: 'cart_item_v2.series_3_band' },
    { url: '/product_images/additional-base-s3.png', labelKey: 'cart_item_v2.series_3_base' },
  ],
  ultra: [
    { url: '/product_images/smart-collar-ultra.png', labelKey: 'hardware_first_box_ultra_device' },
    { url: '/product_images/ultra/unboxing.png', labelKey: 'hardware_first_box_ultra_cable' },
  ],
};

export function WhatsInTheBox({ deviceKey }: { deviceKey: PickYourFiDeviceKey }) {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className={styles.boxHeading}>{t('whats_in_the_box')}</h2>
      <div className={styles.boxGrid}>
        {BOX_CONTENTS[deviceKey].map((item) => (
          <div key={item.url} className={styles.boxCard}>
            <img className={styles.boxImage} src={item.url} alt={t(item.labelKey)} />
            <div className={styles.boxLabel}>{t(item.labelKey)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export { default as ValuePropBoxes } from '../HardwareFirstCart/HardwareFirstValueProps';

/**
 * The pick-your-fi page's per-device detail sections, swapped by the selected card. Box, specs,
 * all-features, owners-say and the app carousel are scoped to this page per the figma; the FAQ
 * reuses the sections the standalone PDPs maintain so its content stays canonical. The behavior
 * band is dog-only - it reads barking, licking and eating - so ultra and cats skip it.
 */
export default function HardwareFirstBelowFold({
  deviceKey,
  species = Species.DOG,
}: {
  deviceKey: PickYourFiDeviceKey;
  species?: Species;
}) {
  return (
    <div className={styles.belowFold}>
      {deviceKey !== 'ultra' && species !== Species.CAT && <HardwareFirstBehaviors />}
      <HardwareFirstSpecs deviceKey={deviceKey} species={species} />
      <HardwareFirstAppCarousel deviceKey={deviceKey} species={species} />
      <HardwareFirstAllFeatures deviceKey={deviceKey} species={species} />
      <HardwareFirstOwnersSay />
      <HardwareFirstQuizBand />
      {/* the shared faq modules key their compact plp treatment off this global class */}
      <div className="hardware-first-faq">
        {deviceKey === 'mini' && <MiniFAQ species={species} />}
        {deviceKey === 's3' && <FAQ collarId={types.series3PlusCollarId} freeTrialEnabled={false} />}
        {deviceKey === 'ultra' && <FAQ collarId={types.ultraCollarId} freeTrialEnabled={false} />}
      </div>
    </div>
  );
}
